import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Paperclip, Trash2, Send, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { C, inp } from "@/lib/claims";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => new Date(iso).toLocaleString("en-SG", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const row = "flex items-start gap-2 min-w-0 rounded-lg bg-slate-950/40 border border-white/[0.05] px-3 py-2 text-sm";

export function ClaimNotes({ claim, onChanged }) {
  const [text, setText] = useState("");
  const { isOwner } = useAuth();
  const add = async () => {
    if (!text.trim()) return;
    try { await axios.post(`${C}/${claim.id}/notes`, { text }); setText(""); onChanged(); } catch (e) { toast.error("Could not add note", { description: e?.response?.data?.detail }); }
  };
  const del = async (nid) => { await axios.delete(`${C}/${claim.id}/notes/${nid}`); onChanged(); };
  const notes = [...(claim.notes || [])].reverse();
  return (
    <div className="space-y-2" data-testid="claim-notes">
      <div className="flex gap-2"><Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a note — e.g. called surveyor, chasing report…" data-testid="note-input" className={`${inp} h-9`} /><Button onClick={add} disabled={!text.trim()} data-testid="note-add" className="h-9 w-9 p-0 bg-rose-500 hover:bg-rose-400 text-slate-950 font-bold shrink-0" title="Add note"><Send className="w-4 h-4" /></Button></div>
      <ul className="space-y-1.5">
        {notes.map((n) => <li key={n.id} className={row} data-testid={`note-${n.id}`}><div className="flex-1 min-w-0"><p className="text-slate-200 break-words leading-snug">{n.text}</p><p className="text-[11px] text-slate-500 font-mono-num mt-0.5">{when(n.at)} · {n.by}</p></div>{isOwner && <button onClick={() => del(n.id)} className="text-slate-600 hover:text-rose-300 mt-0.5" title="Delete note" data-testid={`note-del-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}</li>)}
        {!notes.length && <li className="text-xs text-slate-600 px-1">No notes yet</li>}
      </ul>
    </div>
  );
}

export function ClaimDocuments({ claim, onChanged }) {
  const ref = useRef(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const { isOwner } = useAuth();
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await axios.post(`${C}/${claim.id}/documents`, fd, { params: { label } }); }
      toast.success(`${files.length} document${files.length === 1 ? "" : "s"} attached`); setLabel(""); onChanged();
    } catch (e) { toast.error("Upload failed", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const open = async (doc, download) => {
    const { data } = await axios.get(`${C}/documents/${doc.id}`, { params: download ? { download: 1 } : {}, responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (download) { const a = document.createElement("a"); a.href = url; a.download = doc.original_filename || "document"; a.click(); } else window.open(url, "_blank", "noopener");
  };
  const del = async (doc) => { await axios.delete(`${C}/${claim.id}/documents/${doc.id}`); onChanged(); };
  const docs = claim.documents || [];
  return (
    <div className="space-y-2" data-testid="claim-documents">
      <div className="flex gap-2 items-center">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label — e.g. Survey report, LOD, Offer letter" data-testid="doc-label" className={`${inp} h-9 flex-1 min-w-0`} />
        <input ref={ref} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="doc-file-input" />
        <Button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid="doc-upload-btn" className="h-9 bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold shrink-0"><Paperclip className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">{busy ? "Uploading…" : "Attach"}</span></Button>
      </div>
      <ul className="space-y-1.5">
        {docs.map((doc) => (
          <li key={doc.id} className={`${row} items-center`} data-testid={`doc-${doc.id}`}>
            {doc.content_type === "application/pdf" ? <FileText className="w-4 h-4 text-rose-300 shrink-0" /> : <ImageIcon className="w-4 h-4 text-sky-300 shrink-0" />}
            <button onClick={() => open(doc)} className="flex-1 min-w-0 text-left hover:underline" data-testid={`doc-open-${doc.id}`}><span className="block truncate">{doc.label || doc.original_filename || "Document"}</span><span className="block text-[11px] text-slate-500 font-mono-num truncate">{when(doc.created_at)} · {doc.uploaded_by} · {(doc.size / 1024).toFixed(0)} KB</span></button>
            <button onClick={() => open(doc, true)} className="text-slate-500 hover:text-sky-300 shrink-0" title="Download" data-testid={`doc-download-${doc.id}`}><Download className="w-3.5 h-3.5" /></button>
            {isOwner && <button onClick={() => del(doc)} className="text-slate-600 hover:text-rose-300 shrink-0" title="Delete" data-testid={`doc-del-${doc.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          </li>
        ))}
        {!docs.length && <li className="text-xs text-slate-600 px-1">No documents attached — survey report, LOD, offer letter, payment advice…</li>}
      </ul>
    </div>
  );
}
